import React, { useContext } from 'react';
import styled from 'styled-components';
import { motion } from 'motion/react';
import { FollowupRecommendation } from './recommendations';
import { DescriptionTooltip } from '@/components/tooltip';
import { EditorContext } from '@/features/editor/context/editor-context';
import { DeviceType } from '@/utils/definitions';

interface Props {
  recommendations: FollowupRecommendation[];
  onSelect: (prompt: string) => void;
}

export function FollowupSuggestions({ recommendations, onSelect }: Props) {
  if (!recommendations || recommendations.length === 0) return null;
  const ctx = useContext(EditorContext);
  const device = ctx?.devicePreview?.editorState?.device || DeviceType.Mobile;
  const displayRecs = recommendations.slice(0, 6);

  return (
    <Container>
      {displayRecs.map((rec, index) => (
        <>
          <Pill
            key={rec.prompt}
            className={device === DeviceType.Desktop ? `desktop` : ``}
            onClick={() => onSelect(rec.prompt)}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: index * 0.1,
              duration: 0.4,
              ease: [0.16, 1, 0.3, 1],
            }}
            data-tip
            data-for={`desc${rec.pill}`}
          >
            {rec.pill}
          </Pill>
          <DescriptionTooltip
            id={rec.pill}
            description={rec.prompt}
            place={index === 0 ? `right` : `bottom`}
          />
        </>
      ))}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 1.2rem;
`;

const Pill = styled(motion.button)`
  appearance: none;
  background: white;
  border: 1px solid #e7eaed;
  border-radius: 10rem;
  padding: 1rem 1.5rem;
  font-size: 1.4rem;
  font-family: Inter, sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #1e293b;
  box-shadow: 0 1px 5px 0 rgba(0, 0, 0, 0),
    0 4px 5px 0 rgba(151, 210, 206, 0.22), 0 1px 1px 0 rgba(0, 0, 0, 0.15);
  font-weight: 500;
  user-select: none;

  &.desktop {
    font-size: 1.2rem;
    padding: 0.7rem 1rem;
  }

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  &:active {
    transform: translateY(0);
    background: #f1f5f9;
  }
`;
